import { t } from '../../i18n';
import type { SpaceTimeClient } from '../../spacetime/SpaceTimeClient';
import type { TextInputPopupHandle } from '../TextInputPopup';
import { createWindowFrame } from '../windowing';
import { windowId } from '../windowing';
import { aggregateInventoryRows, buildTradeCsv, moveItemBetweenBuckets } from './mailboxData';
import { renderTradeComposer } from './tradeUi';
import type { TradeGridItem } from './types';

type TradeTargetInventoryRow = { itemId: string; quantity: number; metadataJson: string; tradeable: boolean };
type MyInventoryRow = { itemId: string; quantity: number; metadata: string };

type TradeComposerManagerOptions = {
    spaceTimeClient: SpaceTimeClient;
    inputPopup: TextInputPopupHandle;
    onTradesChanged: () => void;
};

export type TradeComposerManager = {
    openTradeComposer: (rawTargetName: string) => Promise<void>;
    destroyAll: () => void;
};

export function createTradeComposerManager(options: TradeComposerManagerOptions): TradeComposerManager {
    const { spaceTimeClient, inputPopup, onTradesChanged } = options;
    let tradeComposerWindowSeq = 0;
    const tradeComposerWindows = new Set<{ destroy: () => void }>();

    const requestMoveAmount = (item: TradeGridItem, onConfirm: (amount: number) => void): void => {
        inputPopup.show(
            t('userMenu.mailbox.amountPrompt', { max: item.quantity.toString(), name: item.displayName }),
            (text) => {
                const amount = Number.parseInt(text.trim(), 10);
                if (!Number.isFinite(amount) || amount <= 0) {
                    alert(t('userMenu.mailbox.amountInvalid'));
                    return;
                }
                if (amount > item.quantity) {
                    alert(t('userMenu.mailbox.amountTooHigh', { max: item.quantity.toString() }));
                    return;
                }
                onConfirm(amount);
            },
            '1'
        );
    };

    const createTradeComposerWindow = (
        tradeTargetName: string,
        tradeTargetInventoryRows: TradeTargetInventoryRow[],
        myInventoryRows: MyInventoryRow[]
    ): void => {
        const overlay = document.createElement('div');
        Object.assign(overlay.style, {
            position: 'fixed',
            inset: '0',
            display: 'flex',
            backgroundColor: 'transparent',
            pointerEvents: 'none',
            zIndex: '7000',
        });
        const windowContainer = document.createElement('div');
        windowContainer.className = 'window-container';
        const initialSize = {
            width: Math.min(900, Math.max(520, Math.round(window.innerWidth * 0.7))),
            height: Math.min(760, Math.max(420, Math.round(window.innerHeight * 0.76))),
        };
        const initialPosition = {
            x: Math.max(0, Math.round((window.innerWidth - initialSize.width) / 2)),
            y: Math.max(0, Math.round((window.innerHeight - initialSize.height) / 2)),
        };
        Object.assign(windowContainer.style, {
            position: 'fixed',
            left: `${initialPosition.x}px`,
            top: `${initialPosition.y}px`,
            width: `${initialSize.width}px`,
            height: `${initialSize.height}px`,
            pointerEvents: 'auto',
        });
        const composerHeader = document.createElement('div');
        composerHeader.className = 'window-header';
        const title = document.createElement('div');
        title.className = 'window-header-title';
        title.textContent = t('userMenu.mailbox.newTrade');
        const resizeHandle = document.createElement('div');
        resizeHandle.className = 'window-resize-handle';
        resizeHandle.innerHTML =
            '<svg width="10" height="10" viewBox="0 0 10 10" style="pointer-events: none;"><path d="M0 0 L10 0 L10 10 Z" fill="#9ca3af" /></svg>';
        const closeButton = document.createElement('button');
        closeButton.type = 'button';
        closeButton.textContent = '×';
        Object.assign(closeButton.style, {
            marginLeft: 'auto',
            backgroundColor: 'transparent',
            border: 'none',
            color: '#9ca3af',
            fontSize: '1.5rem',
            cursor: 'pointer',
            lineHeight: '1',
            zIndex: '20',
        });
        composerHeader.appendChild(title);
        composerHeader.appendChild(resizeHandle);
        composerHeader.appendChild(closeButton);
        const composerContent = document.createElement('div');
        composerContent.className = 'window-content';
        Object.assign(composerContent.style, {
            height: 'calc(100% - 32px)',
            padding: '12px',
        });
        windowContainer.appendChild(composerHeader);
        windowContainer.appendChild(composerContent);
        overlay.appendChild(windowContainer);
        document.body.appendChild(overlay);

        let tradeMessage = '';
        let tradeableOnlyEnabled = false;
        let myItemsGrid = aggregateInventoryRows(myInventoryRows);
        let myOfferGrid: TradeGridItem[] = [];
        let theirOfferGrid: TradeGridItem[] = [];
        let theirItemsGrid: TradeGridItem[] = [];
        const theirTradeableByItemId = new Map<string, boolean>();
        tradeTargetInventoryRows.forEach((row) => {
            const previous = theirTradeableByItemId.get(row.itemId);
            if (previous === undefined) {
                theirTradeableByItemId.set(row.itemId, row.tradeable);
            } else {
                theirTradeableByItemId.set(row.itemId, previous && row.tradeable);
            }
        });

        const rebuildTheirItemsGridFromFilter = (): void => {
            const filteredRows = tradeTargetInventoryRows.filter((row) => !tradeableOnlyEnabled || row.tradeable);
            const nextTheirItems = aggregateInventoryRows(
                filteredRows.map((row) => ({
                    itemId: row.itemId,
                    quantity: row.quantity,
                    metadata: row.metadataJson,
                    tradeable: row.tradeable,
                }))
            );
            const offeredByItem = new Map<string, number>();
            theirOfferGrid.forEach((item) => {
                offeredByItem.set(item.itemId, (offeredByItem.get(item.itemId) ?? 0) + item.quantity);
            });
            theirItemsGrid = nextTheirItems
                .map((item) => {
                    const offered = offeredByItem.get(item.itemId) ?? 0;
                    const remaining = item.quantity - offered;
                    if (remaining <= 0) return null;
                    return { ...item, quantity: remaining };
                })
                .filter((item): item is TradeGridItem => item !== null);
        };
        rebuildTheirItemsGridFromFilter();

        const frame = createWindowFrame({
            windowId: windowId('trade-composer', 'instance', ++tradeComposerWindowSeq),
            container: windowContainer,
            header: composerHeader,
            resizeHandle,
            closeButton,
            minWidth: 520,
            minHeight: 420,
            initialPosition,
            initialSize,
            persistState: false,
        });
        frame.setWindowState('open');
        const instance = {
            destroy: () => {
                frame.destroy();
                overlay.remove();
            },
        };
        const closeComposer = (): void => {
            tradeComposerWindows.delete(instance);
            instance.destroy();
        };
        closeButton.addEventListener('click', closeComposer);
        tradeComposerWindows.add(instance);

        const renderComposer = (): void => {
            composerContent.innerHTML = '';
            const list = document.createElement('div');
            Object.assign(list.style, { height: '100%' });
            renderTradeComposer({
                list,
                tradeTargetName,
                tradeMessage,
                myItemsGrid,
                myOfferGrid,
                theirItemsGrid,
                theirOfferGrid,
                tradeableOnlyEnabled,
                onToggleTradeableOnly: () => {
                    tradeableOnlyEnabled = !tradeableOnlyEnabled;
                    if (tradeableOnlyEnabled) {
                        const invalidOffered = theirOfferGrid.filter((item) => !theirTradeableByItemId.get(item.itemId));
                        invalidOffered.forEach((item) => {
                            moveItemBetweenBuckets(item.itemId, item.quantity, theirOfferGrid, theirItemsGrid);
                        });
                    }
                    rebuildTheirItemsGridFromFilter();
                    renderComposer();
                },
                requestMoveAmount,
                onTradeMessageChange: (message) => {
                    tradeMessage = message;
                },
                onSubmit: async () => {
                    const hasNonTradeableAskedItems = theirOfferGrid.some(
                        (item) => !theirTradeableByItemId.get(item.itemId)
                    );
                    if (hasNonTradeableAskedItems) {
                        alert(t('userMenu.mailbox.nonTradeableRequested'));
                        return;
                    }
                    const trade = buildTradeCsv(myOfferGrid, theirOfferGrid);
                    const [offered, asked] = trade.split('|');
                    if ((!offered || offered.trim().length === 0) && (!asked || asked.trim().length === 0)) {
                        alert(t('userMenu.mailbox.tradeEmpty'));
                        return;
                    }
                    try {
                        await spaceTimeClient.social.sendMail(tradeTargetName, tradeMessage.trim(), trade);
                        closeComposer();
                        onTradesChanged();
                    } catch (e) {
                        alert(t('userMenu.mailbox.sendTradeFailed', { error: String(e) }));
                    }
                },
                onCancel: () => {
                    closeComposer();
                },
                onMoveFromMyItems: (item, amount) => {
                    moveItemBetweenBuckets(item.itemId, amount, myItemsGrid, myOfferGrid);
                    renderComposer();
                },
                onMoveFromMyOffer: (item, amount) => {
                    moveItemBetweenBuckets(item.itemId, amount, myOfferGrid, myItemsGrid);
                    renderComposer();
                },
                onMoveFromTheirItems: (item, amount) => {
                    moveItemBetweenBuckets(item.itemId, amount, theirItemsGrid, theirOfferGrid);
                    renderComposer();
                },
                onMoveFromTheirOffer: (item, amount) => {
                    moveItemBetweenBuckets(item.itemId, amount, theirOfferGrid, theirItemsGrid);
                    renderComposer();
                },
            });
            composerContent.appendChild(list);
        };

        renderComposer();
    };

    const openTradeComposer = async (rawTargetName: string): Promise<void> => {
        const targetName = rawTargetName.trim();
        if (!targetName) return;

        try {
            const targetInventory = await spaceTimeClient.social.verifyTradeTargetAndFetchInventory(targetName);
            const tradeTargetInventoryRows = targetInventory.map((row) => ({
                itemId: row.itemId,
                quantity: row.quantity,
                metadataJson: row.metadataJson,
                tradeable: row.tradeable,
            }));
            const myInventory = await spaceTimeClient.inventory.fetchPlayerInventory();
            const myRows = myInventory.map((row) => ({
                itemId: row.itemId,
                quantity: row.quantity,
                metadata: row.metadata,
            }));
            createTradeComposerWindow(targetName, tradeTargetInventoryRows, myRows);
        } catch (e) {
            alert(t('userMenu.mailbox.newTradeFailed', { error: String(e) }));
        }
    };

    const destroyAll = (): void => {
        for (const tradeWindow of tradeComposerWindows) {
            tradeWindow.destroy();
        }
        tradeComposerWindows.clear();
    };

    return { openTradeComposer, destroyAll };
}
